"use client";

import React, { useEffect, useRef, useState } from "react";

interface AnimatedTagsProps {
  staticText?: string;
  tags: string[];
  interval?: number; // Time each tag is displayed in milliseconds
  className?: string;
  textColor?: string;
  usePastelColors?: boolean;
}

export default function AnimatedTags({
  staticText = "personalized to you",
  tags,
  interval = 2500, // 2.5 seconds per tag
  className = "",
  textColor = "text-yellow-50",
  usePastelColors = false,
}: AnimatedTagsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const ulRef = useRef<HTMLUListElement>(null);

  // Earth and orange colors for tags (4 colors only)
  const pastelColors = [
    "text-orange-500",
    "text-amber-600",
    "text-orange-600",
    "text-amber-700",
  ];

  useEffect(() => {
    if (tags.length === 0 || !ulRef.current) return;

    const ul = ulRef.current;
    const itemHeight = 1.2; // em units
    let index = 0;

    const slideNext = () => {
      index = (index + 1) % (tags.length + 1); // +1 for duplicate first item
      setCurrentIndex(index % tags.length);

      // If we've reached the duplicate first item (last in list), smoothly transition to it
      if (index === tags.length) {
        // Smoothly move to duplicate first item
        const translateY = -(index * itemHeight);
        ul.style.transition = "transform 0.5s ease-in-out";
        ul.style.transform = `translateY(${translateY}em)`;

        // After transition, instantly jump back to real first item (no transition)
        setTimeout(() => {
          ul.style.transition = "none";
          ul.style.transform = "translateY(0em)";
          index = 0;
          setCurrentIndex(0);
        }, 500); // Wait for transition to complete
      } else {
        // Normal slide to next item
        const translateY = -(index * itemHeight);
        ul.style.transition = "transform 0.5s ease-in-out";
        ul.style.transform = `translateY(${translateY}em)`;
      }
    };

    // Initialize: start at first item (translateY(0))
    ul.style.transform = "translateY(0em)";
    ul.style.transition = "transform 0.5s ease-in-out";

    // Start sliding after initial delay
    const intervalId = setInterval(slideNext, interval);

    return () => clearInterval(intervalId);
  }, [tags.length, interval]);

  if (tags.length === 0) return null;

  return (
    <div
      className="relative inline-block overflow-hidden"
      style={{
        height: "1.2em",
        width: "auto",
      }}
    >
      <ul
        ref={ulRef}
        className="m-0 list-none p-0"
        style={{
          transform: "translateY(0em)",
          transition: "transform 0.5s ease-in-out",
          display: "block",
        }}
      >
        {tags.map((tag, index) => {
          const color = usePastelColors
            ? pastelColors[index % pastelColors.length]
            : textColor;
          return (
            <li
              key={index}
              className={`font-semibold ${color} ${className}`}
              style={{
                display: "block",
                height: "1.2em",
                lineHeight: "1.1em",
                letterSpacing: "-0.0968125rem",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {tag}
            </li>
          );
        })}
        {/* Duplicate first item at the end for seamless loop */}
        <li
          className={`font-semibold ${
            usePastelColors ? pastelColors[0] : textColor
          } ${className}`}
          style={{
            display: "block",
            height: "1.2em",
            lineHeight: "1.1em",
            letterSpacing: "-0.0968125rem",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          {tags[0]}
        </li>
      </ul>
    </div>
  );
}
